import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import DataTable from "../../components/DataTable";
import FilterCuposProducto, {
  FilterCuposProductoFormData,
} from "../../components/FilterCuposProducto";
import { cupoService } from "../../services/cupo/cupoService";
import { useSnackbar } from "../../context/SnackbarContext";
import { CupoProductoClassificationItem } from "../../types/api.types";

const ROWS_PER_PAGE = 10;

export type CupoProductoRow = {
  id: string;
  codigo: string;
  nombre: string;
  tipoProducto: string;
  cupoAsignado: number;
  productClassificationId?: string;
};

function mapApiToRows(
  items: CupoProductoClassificationItem[],
  idOffset = 0,
): CupoProductoRow[] {
  return items.map((item, index) => ({
    id: `${idOffset + index}-${item.code}-${item.nombre}`,
    codigo: item.code,
    nombre: item.nombre,
    tipoProducto: item.tipoProducto,
    cupoAsignado: item.percentage,
    productClassificationId: item.productClassificationId,
  }));
}

function toApiFilters(
  f: FilterCuposProductoFormData | null,
): { code: string; product: string; productType: string } | undefined {
  if (f == null) return undefined;
  return {
    code: f.codigoProducto.trim(),
    product: f.nombreProducto.trim(),
    productType: f.tipoProducto.trim(),
  };
}

const CuposProductoPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [rows, setRows] = useState<CupoProductoRow[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchFilters, setSearchFilters] =
    useState<FilterCuposProductoFormData | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<CupoProductoRow | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  const loadCupos = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await cupoService.getAllCuposPorProducto(
        currentPage,
        ROWS_PER_PAGE,
        toApiFilters(searchFilters),
      );
      setRows(
        mapApiToRows(
          data.classificationResponses ?? [],
          currentPage * ROWS_PER_PAGE,
        ),
      );
      setTotalRegistros(data.total ?? 0);
    } catch {
      setRows([]);
      setTotalRegistros(0);
      showSnackbar("Error al cargar cupos por producto", "error", 4000);
    } finally {
      setIsLoading(false);
    }
  }, [showSnackbar, currentPage, searchFilters]);

  useEffect(() => {
    void loadCupos();
  }, [loadCupos]);

  const handleOpenDelete = (row: CupoProductoRow) => {
    if (!row.productClassificationId?.trim()) {
      showSnackbar(
        "No se puede eliminar: el servidor no devolvió el identificador de clasificación.",
        "error",
        4000,
      );
      return;
    }
    setDeleteDialog(row);
  };

  const handleCloseDeleteDialog = () => {
    if (!deleting) {
      setDeleteDialog(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog?.productClassificationId?.trim()) {
      return;
    }
    setDeleting(true);
    try {
      await cupoService.deleteClassificationByProduct({
        id: deleteDialog.productClassificationId,
      });
      showSnackbar("Cupo eliminado correctamente", "success", 3000);
      setDeleteDialog(null);
      await loadCupos();
    } catch (e: any) {
      showSnackbar(
        e?.response?.data?.message ?? "Error al eliminar el cupo",
        "error",
        4000,
      );
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { header: "Código", key: "codigo" },
    { header: "Nombre", key: "nombre" },
    { header: "Tipo de producto", key: "tipoProducto" },
    {
      header: "Cupo asignado",
      key: "cupoAsignado",
      render: (row: CupoProductoRow) => (
        <Typography variant="body2">% {row.cupoAsignado}</Typography>
      ),
    },
    {
      header: "Acciones",
      key: "actions",
      render: (row: CupoProductoRow) => (
        <IconButton
          size="small"
          color="primary"
          aria-label="Eliminar cupo"
          onClick={() => handleOpenDelete(row)}
          disabled={!row.productClassificationId?.trim()}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleFilter = (data: FilterCuposProductoFormData) => {
    setCurrentPage(0);
    setSearchFilters(data);
  };

  const handleLimpiarFiltros = () => {
    setCurrentPage(0);
    setSearchFilters(null);
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 8,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Cupos por producto
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {searchFilters != null && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={handleLimpiarFiltros}
                >
                  Limpiar filtros
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={() => navigate("/cupos/producto/crear")}
              >
                Crear producto
              </Button>
            </Box>
          </motion.div>

          <FilterCuposProducto
            searchFilters={searchFilters}
            onFilter={handleFilter}
            onCancel={handleLimpiarFiltros}
          />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            style={{ marginTop: 8 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={totalRegistros}
              data={rows}
              page={currentPage + 1}
              rowsPerPage={ROWS_PER_PAGE}
              onChange={(page) => setCurrentPage(page - 1)}
            />
          </motion.div>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog != null}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-cupo-producto-title"
      >
        <DialogTitle id="delete-cupo-producto-title">Eliminar cupo</DialogTitle>
        <DialogContent>
          <DialogContentText component="span">
            ¿Seguro que querés eliminar el cupo? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={() => void handleConfirmDelete()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CuposProductoPage;
